import { faBars, faClose, faDownload, faLanguage, faSignOut } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { getUrlEncodedViewForTab } from "../../../helpers/MyDataHelps/getRootViewForTab";
import { BrandConfiguration, ParticipantStatusModel, ProjectStyleConfiguration, ProjectTabBarModel } from "../../../types/MyDataHelps";
import TabIcon from "../TabIcon/TabIcon";
import UnstyledButton from "../../presentational/UnstyledButton/UnstyledButton";
import "./ParticipantNavigation.css";
import UAParser from "ua-parser-js"
import { putRequest } from "../../../api/requests";
import { makeUrl } from "../../../api/makeUrl";
import LocalizedText from "../../presentational/LocalizedText";
import { Locale } from "../../../types/Locale";

export interface ParticipantNavigationProps {
    colorScheme: "Light" | "Dark"
    selectedTabKey?: string
    participantStatus?: ParticipantStatusModel
    selectedProjectID?: string
    currentLocaleCode: string
    onChangeLocale: (newLocaleCode: string) => void
    brandConfiguration: BrandConfiguration
    preview?: boolean
    sessionID: string
    allLocales: Locale[]
}

interface ResolvedProjectStyles {
    bannerBackgroundColor?: string
    bannerTextColor?: string
    bannerFullWidthLogoUrl?: string | null
}

function getOperatingSystem(): "iOS" | "Android" | "Other" {
    let parser = new UAParser();
    let result = parser.getResult();
    if (result.os.name == "iOS") {
        return "iOS";
    } else if (result.os.name == "Android") {
        return "Android";
    } else {
        return "Other";
    }
}

function applyThemeColor(themeColor: string) {
    var metaTag = document.querySelector('meta[name="theme-color"]');
    if (!metaTag) {
        var viewportTag = document.createElement("meta");
        viewportTag.name = 'theme-color'
        viewportTag.content = themeColor;
        document.getElementsByTagName('head')[0].appendChild(viewportTag);
    } else {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
    }
}

function resolveStyles(styleConfig: ProjectStyleConfiguration | null, organizationColor: string, colorScheme: "Light" | "Dark"): ResolvedProjectStyles {
    let styles: ResolvedProjectStyles = {
        bannerTextColor: organizationColor
    };
    if (colorScheme == "Dark") {
        styles.bannerBackgroundColor = styleConfig?.DarkModeBannerBackgroundColor ?? undefined;
        styles.bannerTextColor = styleConfig?.DarkModeBannerTextColor ?? undefined;
        styles.bannerFullWidthLogoUrl = styleConfig?.DarkModeBannerFullWidthLogoUrl;
    } else {
        styles.bannerBackgroundColor = styleConfig?.BannerBackgroundColor ?? undefined;
        styles.bannerTextColor = styleConfig?.BannerTextColor ?? organizationColor;
        styles.bannerFullWidthLogoUrl = styleConfig?.BannerFullWidthLogoUrl;
    }
    return styles;
}

export default function (props: ParticipantNavigationProps) {
    if (!props.participantStatus) return <div className="participant-navigation" role="banner"></div>;

    const [isOpen, setIsOpen] = useState(false);

    let selectedProject = props.participantStatus.ProjectTabBars.find(p => p.ProjectID == props.selectedProjectID);
    function getProjectBadgeNumber(project: ProjectTabBarModel) {
        return project.Tabs.reduce((acc, tab) => {
            return acc + (tab.BadgeNumber ?? 0);
        }, 0);
    }

    let selectedSystemTab = !selectedProject ? props.participantStatus.Tabs.find(t => t.Key == props.selectedTabKey) : null;
    let otherProjectBadges = props.participantStatus.ProjectTabBars.filter(p => p != selectedProject).reduce((acc, project) => {
        return acc + getProjectBadgeNumber(project);
    }, 0);
    let otherSystemBadges = props.participantStatus.Tabs?.filter(t => t.Key != selectedSystemTab?.Key).reduce((acc, tab) => {
        return acc + (tab.BadgeNumber ?? 0);
    }, 0) ?? 0;
    let badgeCount = otherProjectBadges + otherSystemBadges;

    let styles = selectedProject ? resolveStyles(selectedProject.ProjectStyle, selectedProject.OrganizationColor, props.colorScheme) : {};

    //apply the theme color to allow the browser to use it for the address bar
    let defaultThemeColor = getComputedStyle(document.body)
        .getPropertyValue('--mdh-background-color-0');
    applyThemeColor(styles.bannerBackgroundColor ?? defaultThemeColor);

    let operatingSystem = getOperatingSystem();
    function selectProject(projectID: string) {
        putRequest(makeUrl("api/researchkit/studyparticipant/userpreference/mdh.selectedproject"), { projectID: projectID });
        setIsOpen(false);
    }

    let projectLocales = selectedProject ? props.allLocales.filter(l => selectedProject?.SupportedLocales.includes(l.Code)) : props.allLocales;
    function otherLocale() {
        return projectLocales.find(l => l.Code != props.currentLocaleCode);
    }

    return <div className={"participant-navigation" + (props.preview ? " participant-navigation-preview" : "")} role="banner">
        <UnstyledButton className="header-bar" onClick={() => setIsOpen(!isOpen)} style={{ backgroundColor: styles.bannerBackgroundColor }}>
            {selectedProject &&
                <div className="selected-project">
                    {!styles.bannerFullWidthLogoUrl &&
                        <>
                            <div className="project-image-container">
                                <div className="project-image" role="img" aria-label={`${selectedProject?.ProjectDisplayName} Logo`} style={{ backgroundImage: `url('${selectedProject?.OrganizationLogoUrl}')` }}></div>
                            </div>
                            <div style={{ color: styles.bannerTextColor }} className="project-name">{selectedProject?.ProjectDisplayName}</div>
                        </>
                    }
                    {styles.bannerFullWidthLogoUrl &&
                        <img alt={`${selectedProject?.ProjectDisplayName} Logo`} className="project-wide-logo" src={styles.bannerFullWidthLogoUrl} />
                    }
                </div>
            }
            {!selectedProject && props.participantStatus && selectedSystemTab &&
                <div className="menu-item-component">
                    <TabIcon tabTitle={selectedSystemTab.Title} image={selectedSystemTab.Image} encodedImage={selectedSystemTab.EncodedImage} systemItem={selectedSystemTab.SystemItem} /> {selectedSystemTab.Title}
                </div>
            }
            <div className="open-button" style={{ color: styles.bannerTextColor }}>
                <FontAwesomeIcon style={{ width: "12px" }} icon={isOpen ? faClose : faBars} />
                {badgeCount > 0 && <div className="badge">{badgeCount}</div>}
            </div>
        </UnstyledButton>
        {isOpen &&
            <>
                <div className="menu-dropdown">
                    {(props.participantStatus.ProjectTabBars.length > 1 || (!selectedProject && props.participantStatus.ProjectTabBars.length > 0)) &&
                        <div className="menu-section">
                            <div className="menu-section-header">
                                <LocalizedText textKey={"my-projects-header"} />
                            </div>
                            {props.participantStatus.ProjectTabBars.map(p =>
                                <a onClick={() => selectProject(p.ProjectID)}
                                    href={`#/${p.ProjectID}/${p.Tabs[0].Key}/${getUrlEncodedViewForTab(p.Tabs[0].Type, p.Tabs[0].Properties, props.sessionID)}`}
                                    key={p.PatientID}
                                    className={"menu-item project-menu-item" + (p == selectedProject ? " selected" : "")}>
                                    <div className="menu-item-component menu-item-component-project">
                                        <div className="project-image-container">
                                            <div className="project-image" style={{ backgroundImage: `url('${p.OrganizationLogoUrl}')` }}></div>
                                        </div>
                                        <div className="project-name">{p.ProjectDisplayName}</div>
                                    </div>
                                    <div className="menu-item-component">
                                        {getProjectBadgeNumber(p) > 0 && <div className="badge">{getProjectBadgeNumber(p)}</div>}
                                    </div>
                                </a>
                            )}
                        </div>
                    }
                    <div className="menu-section">
                        {props.brandConfiguration.Name == "MyDataHelps" &&
                            <div className="menu-section-header">MyDataHelps</div>
                        }
                        {operatingSystem != "Android" && !!props.brandConfiguration.AppleAppStoreUrl &&
                            <a target="_blank" className="menu-item" href={props.brandConfiguration.AppleAppStoreUrl}>
                                <div className="menu-item-component">
                                    <FontAwesomeIcon icon={faDownload} /> <LocalizedText textKey={"download-the-app"} /> (iOS)
                                </div>
                            </a>
                        }
                        {operatingSystem != "iOS" && !!props.brandConfiguration.GooglePlayUrl &&
                            <a target="_blank" className="menu-item" href={props.brandConfiguration.GooglePlayUrl}>
                                <div className="menu-item-component">
                                    <FontAwesomeIcon icon={faDownload} /> <LocalizedText textKey={"download-the-app"} /> (Android)
                                </div>
                            </a>
                        }
                        {props.participantStatus.Tabs.map(t =>
                            <a key={t.Key} onClick={() => setIsOpen(false)}
                                className={"menu-item" + (!props.selectedProjectID && props.selectedTabKey == t.Key ? " selected" : "")} href={`#/-/${t.Key}/${getUrlEncodedViewForTab(t.Type, t.Properties, props.sessionID)}`}>
                                <div className="menu-item-component">
                                    <TabIcon tabTitle={t.Title} image={t.Image} encodedImage={t.EncodedImage} systemItem={t.SystemItem} /> {t.Title}
                                </div>
                                <div className="menu-item-component">
                                    {!!t.BadgeNumber && t.BadgeNumber > 0 && <div className="badge">{t.BadgeNumber}</div>}
                                </div>
                            </a>
                        )}
                        {projectLocales.length > 2 &&
                            <div className="menu-item">
                                <div className="menu-item-component">
                                    <FontAwesomeIcon icon={faLanguage} />
                                    <select className="language-select" value={props.currentLocaleCode} onChange={(e) => props.onChangeLocale(e.target.value)}>
                                        {projectLocales.map(l => <option key={l.Code} value={l.Code}>{l.LocalizedName}</option>)}
                                    </select>
                                </div>
                            </div>
                        }
                        {projectLocales.length === 2 &&
                            <UnstyledButton className="menu-item" onClick={() => props.onChangeLocale(otherLocale()!.Code)}>
                                <div className="menu-item-component">
                                    <FontAwesomeIcon icon={faLanguage} /> {otherLocale()!.LocalizedName}
                                </div>
                            </UnstyledButton>
                        }
                        <UnstyledButton className="menu-item" onClick={() => (window as any).ActiveLogout.Logout(true)}>
                            <div className="menu-item-component">
                                <FontAwesomeIcon icon={faSignOut} /> <LocalizedText textKey={"logout-link-label"} />
                            </div>
                        </UnstyledButton>
                        {props.brandConfiguration.Name == "MyDataHelps" && props.brandConfiguration.Logo &&
                            <div className="mydatahelps-brand">
                                {props.colorScheme == "Dark" &&
                                    <img alt={props.brandConfiguration.Logo.Alt} src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/MHD-Logo-WHITE.svg" />
                                }
                                {props.colorScheme == "Light" &&
                                    <img alt={props.brandConfiguration.Logo.Alt} src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/MHD-Logo-BLK.svg" />
                                }
                            </div>
                        }
                    </div>
                </div>
                <div className="menu-overlay">
                </div>
                <div className="menu-overlay-blur"></div>
            </>
        }
    </div>
}